import {
	GLOBAL_ADMIN_PERMISSION,
	GlobalAdminGrantee,
	mayBeGrantedGlobalAdmin,
	mayHoldGlobalAdmin,
	permissionsMissingFor,
	roleCarriesGlobalAdmin,
	staffGranteeQualifies,
} from "@signalist/shared-types";

import { UserRole } from "@/pages/Private/pages/UserRoles/schema/roles";

type RoleLike = Pick<UserRole, "code" | "permissions">;

/** The signed-in user's permission codes, from their roles. */
const permissionCodesOf = (roles: readonly RoleLike[]): string[] =>
	roles.flatMap((role) => (role.permissions ?? []).map((permission) => permission.code));

/**
 * The roles the Invite and Edit user modals offer, mirroring the API's `roleGrantRefusal`
 * (apps/api/src/roles/utils/role-grant.util.ts) through the shared rules:
 * - a role carrying GLOBAL_ADMIN only to staff, and only for a grantee `mayBeGrantedGlobalAdmin`
 *   accepts: a confirmed staff-domain (@signalist.io, @digital-now.io, @signalist.de) address, or a new invitee (PREC-4636);
 * - for a caller without GLOBAL_ADMIN, no role holding a permission the caller lacks (PREC-4634:
 *   production's USER role holds INVITE_USER, so a seat could invite a COMPANY_ADMIN).
 */
export const rolesOfferedFor = <T extends RoleLike>(
	roles: readonly T[],
	grantee: GlobalAdminGrantee,
	callerRoles: readonly RoleLike[]
): T[] => {
	const callerCodes = permissionCodesOf(callerRoles);
	const callerIsStaff = callerCodes.includes(GLOBAL_ADMIN_PERMISSION);

	return roles.filter((role) =>
		roleCarriesGlobalAdmin(role)
			? callerIsStaff && mayBeGrantedGlobalAdmin(grantee)
			: callerIsStaff || permissionsMissingFor(role, callerCodes).length === 0
	);
};

/**
 * Staff roles the user already holds but may not be granted again: shown in the Edit user modal,
 * selected and not pickable, so staff can see them and switch the user off them (PREC-4636).
 */
export const heldStaffRolesNotOffered = <T extends RoleLike>(
	heldRoles: readonly T[],
	offered: readonly RoleLike[]
): T[] =>
	heldRoles.filter(
		(role) => roleCarriesGlobalAdmin(role) && !offered.some((option) => option.code === role.code)
	);

/** Why a held staff role is not offered, for its label; null when the caller simply may not grant it. */
export const heldStaffRoleNote = (grantee: GlobalAdminGrantee): string | null => {
	if (!mayHoldGlobalAdmin(grantee.email)) {
		return "not allowed for this email";
	}

	return staffGranteeQualifies(grantee) ? null : "email not confirmed";
};
